// FloatingDock — pinned to the bottom of the viewport while the user is
// scrolled inside the records section of the Person page. Auto-hides
// when scrolled above (still in editorial content) or below (past the
// records). Three sections:
//
//   • View tabs   — Cards / Table / Visualize / Capture log / Screenshots
//   • Filter chip — opens a popover with source pills + open-only toggle
//   • ACTIONS button — opens a dropdown menu with all 5 export actions
//                      (Intake packet / Full PDF / Expungement packet /
//                       Personal details / MVC abstract request)
//
// Filters + view tabs are passed in as props from PersonPage so the
// state stays single-sourced. Visibility is controlled by the parent
// via `visible` prop (computed from scroll position via two sentinel
// refs in PersonPage — start + end of records section).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { JSX } from "react";
import { showDownloadToast } from "../lib/downloadToast";

// Pick a verb prefix for the download toast based on the leaf id.
// "Generate", "Build", or "Download" depending on what the user is
// triggering — small wording win that makes the toast feel intentional.
function inferToastVerb(id: string): string {
  if (id.includes("packet") || id.includes("zip")) return "Building";
  if (id.includes("preview")) return "Opening";
  if (id.includes("petition") || id.includes("letter") || id.includes("pdf") || id.includes("cs-") || id.includes("do21")) return "Generating";
  return "Downloading";
}

export type ViewMode = "cards" | "table" | "visualize" | "log" | "screenshots";

export interface SourceBucket {
  key: string;
  label: string;
  count: number;
}

interface Props {
  visible: boolean;

  // View
  view: ViewMode;
  onViewChange: (v: ViewMode) => void;
  viewCounts: {
    cards: number;
    table: number;
    visualize?: number;
    log: number;
    screenshots: number;
  };

  // Filters (only relevant for Cards / Table views)
  filtersApplicable: boolean;
  showSourceFilter: boolean;
  sourceBuckets: SourceBucket[];      // includes "all" option as first
  activeSourceFilter: string;
  onSourceFilterChange: (key: string) => void;
  showOpenOnly: boolean;
  openOnly: boolean;
  openCount: number;
  totalCount: number;
  onOpenOnlyChange: (v: boolean) => void;

  // Actions menu
  jobId: string;
  showExpungementActions: boolean;
  onIntakePacket: () => void;
  onFullPdf: () => void;
  pdfState: "idle" | "generating" | "error";
  onExpungementPacket: () => void;
  packetState: "idle" | "generating" | "error";
  /** Direct download URL for the pre-filled CN-10557 expungement
   *  petition PDF (single-PDF, AcroForm-fillable, no ZIP). Sibling to
   *  onExpungementPacket — handles the common single-document filing
   *  workflow without the .docx supplement bundle. Caller passes a
   *  plain href; the dock renders it as a download link. */
  expungementPetitionUrl?: string;
  onPersonalDetails: () => void;
  mvcDo21Url: string;
  /** Direct URL to the client-takeaway summary PDF (the
   *  "Where you stand today" 2-3 page document for the client). */
  clientSummaryPdfUrl: string;
  /** Direct URL to the in-browser preview of the client summary
   *  (same React page, no PDF wrapper). */
  clientSummaryPreviewUrl: string;
  /** Direct download URL for the screenshot packet PDF. Caller builds
   *  it (so it can include source / openOnly query params reflecting
   *  the current view's filters). When omitted, the action is hidden. */
  screenshotPacketHref?: string;
  /** Optional override for the action label. */
  screenshotPacketLabel?: string;
  /** Optional sub-label. */
  screenshotPacketSub?: string;
  /** Open the time-served / fine-waiver letter generator modal. */
  onFineWaiverLetter: () => void;
  /** Open the warrant-recall letter generator modal. */
  onWarrantLetter: () => void;
  /** Open the resume builder modal — paste-to-PDF with editable preview. */
  onResumeBuilder: () => void;
  /** Open the inline note-compose box on the Person page (scrolls to
   *  the Notes section + autofocuses the textarea). */
  onAddNote: () => void;
  /** Open the profile-edit modal (every intake field — name, DOB,
   *  address, license, SSN, ZIP, …). Used to be a standalone
   *  secondary button row on the page; consolidated here. */
  onEditProfile: () => void;
}

// Tabs are ordered by intake priority: the three "data about the
// person" views first (Cards, Table, Shots), then the analytic /
// operational views (Visualize, Log). The first three all accept the
// dock's filter (source + open-only).
const VIEW_TAB_DEFS: Array<{ key: ViewMode; label: string; shortLabel?: string; icon: JSX.Element }> = [
  {
    key: "cards",
    label: "Cards",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
        <rect x="9" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
        <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
        <rect x="9" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
  },
  {
    key: "table",
    label: "Table",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="2.5" width="13" height="11" rx="1" stroke="currentColor" strokeWidth="1.4" />
        <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.2" />
        <line x1="1.5" y1="10" x2="14.5" y2="10" stroke="currentColor" strokeWidth="1.2" />
        <line x1="6" y1="2.5" x2="6" y2="13.5" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    key: "screenshots",
    label: "Shots",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1.5" y="3.5" width="13" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.3" />
        <rect x="6.5" y="2" width="3" height="2" rx="0.4" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ),
  },
  {
    key: "visualize",
    label: "Visualize",
    shortLabel: "Viz",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2 13L5.5 9L8 11L12 5L14 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <circle cx="2" cy="13" r="1.4" fill="currentColor" />
        <circle cx="5.5" cy="9" r="1.4" fill="currentColor" />
        <circle cx="8" cy="11" r="1.4" fill="currentColor" />
        <circle cx="12" cy="5" r="1.4" fill="currentColor" />
        <circle cx="14" cy="7" r="1.4" fill="currentColor" />
      </svg>
    ),
  },
  {
    key: "log",
    label: "Log",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="2" y="2.5" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.4" />
        <line x1="4.5" y1="5.5" x2="11.5" y2="5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="4.5" y1="8" x2="11.5" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="4.5" y1="10.5" x2="9" y2="10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
  },
];

export function FloatingDock(props: Props): JSX.Element {
  const [filterOpen, setFilterOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const dockRef = useRef<HTMLDivElement | null>(null);

  // Three sizing tiers based on viewport width:
  //   compact (< 520):  phones — drop labels, square icon-only buttons
  //   standard (520-1023): tablets — labeled buttons, current sizes
  //   large (≥ 1024):  desktop — bumped buttons + bigger labels (we have
  //                    the horizontal headroom and want the dock readable
  //                    at arm's length / from a wide screen).
  const [tier, setTier] = useState<"compact" | "standard" | "large">(() => {
    if (typeof window === "undefined") return "standard";
    if (window.innerWidth < 520) return "compact";
    if (window.innerWidth >= 1024) return "large";
    return "standard";
  });
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setTier(w < 520 ? "compact" : w >= 1024 ? "large" : "standard");
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const compact = tier === "compact";
  const large = tier === "large";

  // Close popovers on outside-click
  useEffect(() => {
    if (!filterOpen && !actionsOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (dockRef.current && !dockRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
        setActionsOpen(false);
      }
    };
    window.addEventListener("mousedown", onDocClick);
    return () => window.removeEventListener("mousedown", onDocClick);
  }, [filterOpen, actionsOpen]);

  // Close popovers on Escape
  useEffect(() => {
    if (!filterOpen && !actionsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setFilterOpen(false);
        setActionsOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filterOpen, actionsOpen]);

  const visible = props.visible;
  const showFilterChip = props.filtersApplicable && (props.showSourceFilter || props.showOpenOnly);

  // Render via portal directly into document.body so the fixed
  // positioning isn't broken by any ancestor with `transform` /
  // `filter` / `will-change`, which create a new containing block for
  // fixed-positioned descendants. Was hitting this with the `fade-in`
  // animation on the parent <main>.
  const dock = (
    <div
      ref={dockRef}
      data-floating-dock
      className="no-print"
      style={{
        position: "fixed",
        bottom: compact ? 10 : 18,
        // Span the viewport so we can reliably center the dock with
        // flex regardless of its intrinsic width — fixes the "pushed
        // off to one side" bug on phones where the inner dock was
        // wider than the centering math expected.
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        transform: visible ? "translateY(0)" : "translateY(calc(100% + 32px))",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        transition: "transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.25s ease",
        zIndex: 50,
        // Tight outer padding on mobile so the dock can use the
        // maximum viewport width before its own pill border kicks in.
        // Was 8px which was eating 16px of horizontal budget, pushing
        // the dock past 375px iPhone SE width and causing the page to
        // gain horizontal scroll.
        paddingInline: compact ? 4 : 8,
      }}
    >
      <div
        className="dark-tile"
        style={{
          background: "rgba(20,18,16,0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: 999,
          padding: compact ? "5px 6px" : large ? "10px 14px" : "8px 10px",
          display: "flex",
          alignItems: "center",
          gap: compact ? 1 : large ? 6 : 4,
          boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
          maxWidth: "100%",
        }}
      >
        {/* — VIEW TAB ICONS — */}
        {VIEW_TAB_DEFS.map((t) => {
          const count = props.viewCounts[t.key];
          const isActive = props.view === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                props.onViewChange(t.key);
                setFilterOpen(false);
                setActionsOpen(false);
              }}
              title={`${t.label}${count != null ? ` · ${count}` : ""}`}
              className="group inline-flex flex-col items-center justify-center rounded-full transition"
              style={{
                // Three sizing tiers (see `tier` state):
                //   compact (<520):   40×40 square icon-only — phones
                //   standard (520+):  52×44 with small mono label — tablets
                //   large (≥1024):    66×56 with bigger label — desktop
                width: compact ? 40 : large ? 66 : 52,
                height: compact ? 40 : large ? 56 : 44,
                // No background change on the active tab anymore — the
                // light-blue text color is enough of a "pop" indicator.
                // Keeps the dock visually quiet.
                background: "transparent",
                color: isActive ? "#7ec5ff" : "rgba(255,255,255,0.65)",
                cursor: "pointer",
                border: "none",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = "rgba(255,255,255,0.9)";
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = "rgba(255,255,255,0.65)";
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // Scale the SVG icon per tier (SVG is fixed at 16px).
                  transform: compact ? "scale(1.3)" : large ? "scale(1.4)" : "scale(1)",
                }}
              >
                {t.icon}
              </span>
              {!compact && (
                <span
                  style={{
                    // Italic Fraunces matches the nav verbs — gives the
                    // dock the same editorial voice as the rest of the
                    // app (was DM Mono, which felt too "tactical").
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: large ? 13 : 11,
                    marginTop: large ? 5 : 2,
                    letterSpacing: "0.005em",
                    fontWeight: isActive ? 600 : 500,
                    fontVariationSettings: '"opsz" 36, "SOFT" 50',
                    lineHeight: 1,
                  }}
                >
                  {/* Use shortLabel when available at the standard
                      (non-large) tier so long words don't overflow on
                      narrow phones (e.g. "Visualize" → "Viz"). */}
                  {!large && t.shortLabel ? t.shortLabel : t.label}
                </span>
              )}
            </button>
          );
        })}

        {/* Separator */}
        <span style={{ width: 1, height: compact ? 24 : large ? 36 : 28, background: "rgba(255,255,255,0.14)", margin: compact ? "0 1px" : large ? "0 6px" : "0 4px" }} />

        {/* — FILTER CHIP — opens popover */}
        {showFilterChip ? (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => {
                setFilterOpen(!filterOpen);
                setActionsOpen(false);
              }}
              title="Filter records"
              className="rounded-full inline-flex items-center justify-center gap-1.5 transition relative"
              style={{
                // Same three-tier sizing as the view-tab buttons; on
                // compact this is a square dot button, on standard/large
                // it widens to show active filter labels.
                width:
                  compact
                    ? 40
                    : filterOpen || (props.activeSourceFilter !== "all" || props.openOnly)
                    ? "auto"
                    : large ? 56 : 44,
                paddingLeft: compact ? 0 : large ? 16 : 12,
                paddingRight: compact ? 0 : large ? 16 : 12,
                height: compact ? 40 : large ? 56 : 44,
                background:
                  filterOpen
                    ? "rgba(255,255,255,0.14)"
                    : (props.activeSourceFilter !== "all" || props.openOnly)
                    ? "rgba(232,160,80,0.15)"
                    : "transparent",
                color:
                  (props.activeSourceFilter !== "all" || props.openOnly)
                    ? "var(--color-amber-hi)"
                    : "rgba(255,255,255,0.7)",
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-mono)",
                fontSize: large ? 13 : 11,
                fontWeight: 600,
              }}
            >
              <svg
                width={compact ? 18 : large ? 18 : 14}
                height={compact ? 18 : large ? 18 : 14}
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden="true"
              >
                <path d="M1 2H13L9 7V12L5 13V7L1 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              </svg>
              {/* Compact mode: tiny dot indicator when filters are active */}
              {compact && (props.activeSourceFilter !== "all" || props.openOnly) && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--color-amber-hi)",
                    boxShadow: "0 0 0 2px rgba(20,18,16,1)",
                  }}
                />
              )}
              {/* Full mode: text label expansion when filters are active */}
              {!compact && (props.activeSourceFilter !== "all" || props.openOnly) && (
                <span>
                  {[
                    props.activeSourceFilter !== "all" ? props.sourceBuckets.find((b) => b.key === props.activeSourceFilter)?.label : null,
                    props.openOnly ? "Outstanding" : null,
                  ].filter(Boolean).join(" · ")}
                </span>
              )}
            </button>

            {filterOpen && (
              <FilterPopover
                showSourceFilter={props.showSourceFilter}
                sourceBuckets={props.sourceBuckets}
                activeSourceFilter={props.activeSourceFilter}
                onSourceFilterChange={(k) => {
                  props.onSourceFilterChange(k);
                  setFilterOpen(false);
                }}
                showOpenOnly={props.showOpenOnly}
                openOnly={props.openOnly}
                openCount={props.openCount}
                totalCount={props.totalCount}
                onOpenOnlyChange={(v) => {
                  props.onOpenOnlyChange(v);
                  // Don't close — user may want to also tweak source
                }}
              />
            )}
          </div>
        ) : null}

        {/* — ACTIONS — opens dropdown */}
        <div style={{ position: "relative" }}>
          <button
            type="button"
            data-tour="actions-button"
            onClick={() => {
              setActionsOpen(!actionsOpen);
              setFilterOpen(false);
            }}
            title="Actions menu"
            className="rounded-full inline-flex items-center justify-center transition"
            style={{
              padding: compact ? "0 9px" : large ? "0 22px" : "0 16px",
              height: compact ? 40 : large ? 56 : 44,
              // Primary action — Editorial Quiet terracotta, was rose-pink.
              background: actionsOpen
                ? "var(--color-terracotta-hi)"
                : "color-mix(in oklab, var(--color-terracotta-hi) 88%, transparent)",
              color: "white",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: compact ? 10 : large ? 14 : 12,
              fontWeight: 700,
              letterSpacing: compact ? "0.04em" : "0.08em",
              gap: compact ? 3 : 6,
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {/* Compact: arrow icon + ACT abbreviation if even tighter,
                else full ACTIONS label. Most phones (375-414px wide)
                have plenty of room for "ACTIONS" once we drop the view-
                tab labels — but keep the option to abbreviate if it
                ever gets squeezed. */}
            {compact ? (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M7 1V10M7 10L3 6M7 10L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>ACT</span>
              </>
            ) : (
              <>
                ACTIONS
                <span style={{ fontSize: 9, opacity: 0.85 }}>{actionsOpen ? "▲" : "▼"}</span>
              </>
            )}
          </button>

          {actionsOpen && (
            <ActionsMenu
              jobId={props.jobId}
              showExpungementActions={props.showExpungementActions}
              onIntakePacket={() => { props.onIntakePacket(); setActionsOpen(false); }}
              onFullPdf={() => { props.onFullPdf(); setActionsOpen(false); }}
              pdfState={props.pdfState}
              onExpungementPacket={() => { props.onExpungementPacket(); setActionsOpen(false); }}
              packetState={props.packetState}
              expungementPetitionUrl={props.expungementPetitionUrl}
              onPersonalDetails={() => { props.onPersonalDetails(); setActionsOpen(false); }}
              mvcDo21Url={props.mvcDo21Url}
              clientSummaryPdfUrl={props.clientSummaryPdfUrl}
              clientSummaryPreviewUrl={props.clientSummaryPreviewUrl}
              screenshotPacketHref={props.screenshotPacketHref}
              screenshotPacketLabel={props.screenshotPacketLabel}
              screenshotPacketSub={props.screenshotPacketSub}
              onFineWaiverLetter={() => { props.onFineWaiverLetter(); setActionsOpen(false); }}
              onWarrantLetter={() => { props.onWarrantLetter(); setActionsOpen(false); }}
              onResumeBuilder={() => { props.onResumeBuilder(); setActionsOpen(false); }}
              onAddNote={() => { props.onAddNote(); setActionsOpen(false); }}
              onEditProfile={() => { props.onEditProfile(); setActionsOpen(false); }}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Mount into <body> so fixed-positioning isn't broken by ancestor
  // transforms (the page-load fade-in on <main> was creating a
  // containing block that anchored the dock to the document instead
  // of the viewport).
  if (typeof document === "undefined") return dock;
  return createPortal(dock, document.body);
}

// — Filter popover (source pills + open-only) -----------------------------

function FilterPopover({
  showSourceFilter,
  sourceBuckets,
  activeSourceFilter,
  onSourceFilterChange,
  showOpenOnly,
  openOnly,
  openCount,
  totalCount,
  onOpenOnlyChange,
}: {
  showSourceFilter: boolean;
  sourceBuckets: SourceBucket[];
  activeSourceFilter: string;
  onSourceFilterChange: (k: string) => void;
  showOpenOnly: boolean;
  openOnly: boolean;
  openCount: number;
  totalCount: number;
  onOpenOnlyChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div
      className="dark-tile"
      style={{
        position: "absolute",
        bottom: "calc(100% + 12px)",
        // Anchor to the RIGHT edge of the filter chip so the popover
        // extends LEFT (toward the dock's center / view-tab icons),
        // which on narrow mobile keeps it inside the viewport. Anchored
        // `left: 0` previously, which sent the 240px popover off the
        // right edge of the screen on phones (chip sits middle-right of
        // the dock pill). `maxWidth: calc(100vw - 16px)` is a safety net
        // for any edge case where the chip itself sits very close to
        // the left edge.
        right: 0,
        maxWidth: "calc(100vw - 16px)",
        background: "rgba(20,18,16,0.97)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 12,
        padding: "14px 16px",
        boxShadow: "0 16px 50px rgba(0,0,0,0.55)",
        minWidth: 280,
        animation: "dock-pop-in 0.18s ease-out both",
      }}
    >
      <style>{`
        @keyframes dock-pop-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {showSourceFilter && (
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontFamily: "var(--font-mono)",
              color: "rgba(255,255,255,0.55)",
              marginBottom: 8,
            }}
          >
            Source
          </div>
          <div className="flex flex-wrap gap-2">
            {sourceBuckets.map((b) => {
              const isActive = activeSourceFilter === b.key;
              return (
                <button
                  key={b.key}
                  onClick={() => onSourceFilterChange(b.key)}
                  className={`rounded-md px-3 py-1.5 text-[14px] font-semibold transition ${
                    isActive
                      ? "bg-white/15 text-white"
                      : "text-[color:var(--color-text-muted)] hover:text-white hover:bg-white/5"
                  }`}
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                >
                  {b.label}
                  <span className="tabular ml-2 text-[12px] opacity-65">{b.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showOpenOnly && (
        <div style={{ marginTop: showSourceFilter ? 14 : 0 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontFamily: "var(--font-mono)",
              color: "rgba(255,255,255,0.55)",
              marginBottom: 8,
            }}
          >
            Status
          </div>
          <button
            onClick={() => onOpenOnlyChange(!openOnly)}
            title="Hides cases that are Disposed or Transferred to another court. Everything else shows."
            className={`inline-flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-[14px] font-semibold transition ${
              openOnly
                ? "border-[#e8a050] bg-[#e8a050]/15 text-[#e8a050]"
                : "border-white/10 text-[color:var(--color-text-muted)] hover:text-white"
            }`}
          >
            <span
              aria-hidden="true"
              style={{
                width: 11,
                height: 11,
                borderRadius: "50%",
                background: openOnly ? "var(--color-amber-hi)" : "transparent",
                border: openOnly ? "none" : "1.5px solid currentColor",
              }}
            />
            <span>Outstanding court records</span>
            <span className="tabular ml-auto text-[13px] opacity-70">
              {openCount} of {totalCount}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

// — Actions dropdown menu --------------------------------------------------

function ActionsMenu({
  jobId,
  showExpungementActions,
  onIntakePacket,
  onFullPdf,
  pdfState,
  onExpungementPacket,
  packetState,
  expungementPetitionUrl,
  onPersonalDetails,
  mvcDo21Url,
  clientSummaryPdfUrl,
  clientSummaryPreviewUrl,
  screenshotPacketHref,
  screenshotPacketLabel,
  screenshotPacketSub,
  onFineWaiverLetter,
  onWarrantLetter,
  onResumeBuilder,
  onAddNote,
  onEditProfile,
}: {
  jobId: string;
  showExpungementActions: boolean;
  onIntakePacket: () => void;
  onFullPdf: () => void;
  pdfState: "idle" | "generating" | "error";
  onExpungementPacket: () => void;
  packetState: "idle" | "generating" | "error";
  expungementPetitionUrl?: string;
  onPersonalDetails: () => void;
  mvcDo21Url: string;
  clientSummaryPdfUrl: string;
  clientSummaryPreviewUrl: string;
  screenshotPacketHref?: string;
  screenshotPacketLabel?: string;
  screenshotPacketSub?: string;
  onFineWaiverLetter: () => void;
  onWarrantLetter: () => void;
  onResumeBuilder: () => void;
  onAddNote: () => void;
  onEditProfile: () => void;
}): JSX.Element {
  // ── Color "lanes" ─────────────────────────────────────────────────
  // Three top categories, each a hue. The tone cascades down so a leaf
  // on the third level still reads as "this is in the meeting category"
  // by its color.
  type Tone = { hi: string; tile: string; tileHot: string; label: string };
  const TONES = {
    meeting: {
      hi: "var(--color-blue-hi)",
      tile: "rgba(96,165,250,0.12)",
      tileHot: "rgba(96,165,250,0.22)",
      label: "#cfe3ff",
    },
    casework: {
      hi: "var(--color-orange-hi)",
      tile: "rgba(251,146,60,0.12)",
      tileHot: "rgba(251,146,60,0.22)",
      label: "#ffd9b8",
    },
    internal: {
      hi: "rgba(255,255,255,0.78)",
      tile: "rgba(255,255,255,0.06)",
      tileHot: "rgba(255,255,255,0.10)",
      label: "rgba(255,255,255,0.92)",
    },
  } as const satisfies Record<string, Tone>;

  // ── Tree definition ────────────────────────────────────────────────
  // Discriminated union: each node is either a leaf (action) or a
  // branch (drills into a submenu). Branches can nest to any depth.
  type Leaf = {
    kind: "leaf";
    id: string;
    label: string;
    sub: string;
    icon: JSX.Element;
    tone: Tone;
    critical?: boolean;
    beta?: boolean;
    href?: string;
    onClick?: () => void;
    download?: boolean;
    busy?: boolean;
    busyLabel?: string;
  };
  type Branch = {
    kind: "branch";
    id: string;
    label: string;
    sub: string;
    icon: JSX.Element;
    tone: Tone;
    children: Node[];
  };
  type Node = Leaf | Branch;

  const root: Branch = {
    kind: "branch",
    id: "root",
    label: "Actions",
    sub: "",
    icon: <span />,
    tone: TONES.meeting,
    children: [
      // ── Top 1: In the meeting ────────────────────────────────────
      {
        kind: "branch",
        id: "meeting",
        label: "In the meeting",
        sub: "Live transcription, hand-outs, resume",
        icon: ICON_MIC,
        tone: TONES.meeting,
        children: [
          {
            kind: "leaf",
            id: "start-meeting",
            label: "Start live transcription",
            sub: "AI transcribes the conversation and auto-fills the participant's profile.",
            icon: ICON_REC,
            tone: TONES.meeting,
            critical: true,
            href: `/assessment/${jobId}`,
          },
          {
            kind: "branch",
            id: "handouts",
            label: "Hand-outs",
            sub: "Documents the participant takes with them",
            icon: ICON_HANDOUT,
            tone: TONES.meeting,
            children: [
              {
                kind: "leaf",
                id: "cs-en",
                label: "Client summary: English",
                sub: "Plain-language 2-page PDF. Print and hand to them.",
                icon: ICON_DOC,
                tone: TONES.meeting,
                href: clientSummaryPdfUrl,
              },
              {
                kind: "leaf",
                id: "cs-es",
                label: "Client summary: Spanish",
                sub: "Same 2-page summary, in Spanish.",
                icon: ICON_DOC_ES,
                tone: TONES.meeting,
                href: `${clientSummaryPdfUrl}${clientSummaryPdfUrl.includes("?") ? "&" : "?"}lang=es`,
              },
              {
                kind: "leaf",
                id: "cs-preview",
                label: "Preview client summary",
                sub: "Open in browser to proofread before printing.",
                icon: ICON_EYE,
                tone: TONES.meeting,
                href: clientSummaryPreviewUrl,
              },
            ],
          },
          {
            kind: "leaf",
            id: "resume",
            label: "Build a resume",
            sub: "Quick survey → polished one-page resume PDF.",
            icon: ICON_RESUME,
            tone: TONES.meeting,
            beta: true,
            onClick: onResumeBuilder,
          },
        ],
      },
      // ── Top 2: Take action on their case ─────────────────────────
      {
        kind: "branch",
        id: "casework",
        label: "Take action on their case",
        sub: "Letters, abstracts, expungement filing",
        icon: ICON_PAPER_PLANE,
        tone: TONES.casework,
        children: [
          {
            kind: "branch",
            id: "letters",
            label: "Court letters",
            sub: "Send to the municipal court",
            icon: ICON_ENVELOPE,
            tone: TONES.casework,
            children: [
              {
                kind: "leaf",
                id: "fine-letter",
                label: "Fine-waiver letter",
                sub: "Asks the court to waive outstanding fines based on time served. Editable .docx on NJRC letterhead.",
                icon: ICON_LETTER_DOLLAR,
                tone: TONES.casework,
                critical: true,
                onClick: onFineWaiverLetter,
              },
              {
                kind: "leaf",
                id: "warrant-letter",
                label: "Warrant-recall letter",
                sub: "Asks the court to recall the warrant and schedule a hearing. Editable .docx.",
                icon: ICON_LETTER_DOT,
                tone: TONES.casework,
                onClick: onWarrantLetter,
              },
            ],
          },
          {
            kind: "leaf",
            id: "mvc-do21",
            label: "MVC abstract request (DO-21)",
            sub: "Request the participant's full driving record. They sign and mail it in.",
            icon: ICON_CAR,
            tone: TONES.casework,
            href: mvcDo21Url,
          },
          ...(showExpungementActions
            ? ([
                {
                  kind: "branch",
                  id: "expungement",
                  label: "Expungement filing",
                  sub: "Confirm details, then file",
                  icon: ICON_SHIELD,
                  tone: TONES.casework,
                  children: [
                    {
                      kind: "leaf" as const,
                      id: "exp-confirm",
                      label: "① Confirm petitioner details",
                      sub: "Address, employer, family, required to fill the petition + fee waiver.",
                      icon: ICON_PERSON,
                      tone: TONES.casework,
                      onClick: onPersonalDetails,
                    },
                    ...(expungementPetitionUrl
                      ? [{
                          kind: "leaf" as const,
                          id: "exp-petition",
                          label: "② Pre-filled petition (CN-10557)",
                          sub: "Just the petition. Court-ready PDF, fields editable.",
                          icon: ICON_DOC_CHECK,
                          tone: TONES.casework,
                          critical: true,
                          href: expungementPetitionUrl,
                          download: true,
                        }]
                      : []),
                    {
                      kind: "leaf" as const,
                      id: "exp-packet",
                      label: "③ Full filing packet (.zip)",
                      sub: "Petition + fee waiver + Form 9 + service labels, everything to file.",
                      icon: ICON_PACKET,
                      tone: TONES.casework,
                      beta: true,
                      busy: packetState === "generating",
                      busyLabel: "Building packet…",
                      onClick: onExpungementPacket,
                    },
                  ],
                },
              ] as Node[])
            : []),
        ],
      },
      // ── Top 3: For our records ───────────────────────────────────
      {
        kind: "branch",
        id: "internal",
        label: "For our records",
        sub: "Notes + case-file downloads",
        icon: ICON_FOLDER,
        tone: TONES.internal,
        children: [
          {
            kind: "leaf",
            id: "edit-profile",
            label: "Edit participant profile",
            sub: "Name, DOB, address, license, SSN, ZIP, phone, every intake field in one place.",
            icon: ICON_PERSON,
            tone: TONES.internal,
            onClick: onEditProfile,
          },
          {
            kind: "leaf",
            id: "add-note",
            label: "Add a note",
            sub: "Jot something on this person, observations, follow-ups, anything to remember.",
            icon: ICON_NOTE,
            tone: TONES.internal,
            critical: true,
            onClick: onAddNote,
          },
          {
            kind: "leaf",
            id: "full-pdf",
            label: "Full PDF report",
            sub: pdfState === "error" ? "Last attempt failed: try again." : "Every record on file in one printable PDF.",
            icon: ICON_DOWNLOAD,
            tone: TONES.internal,
            busy: pdfState === "generating",
            busyLabel: "Generating PDF…",
            onClick: onFullPdf,
          },
          {
            kind: "leaf",
            id: "intake-packet",
            label: "Intake packet",
            sub: "Single-page summary: handy as a meeting cheat-sheet.",
            icon: ICON_CLIPBOARD,
            tone: TONES.internal,
            onClick: onIntakePacket,
            href: `/intake/${jobId}?print=1`,
          },
          ...(screenshotPacketHref
            ? [{
                kind: "leaf" as const,
                id: "shots",
                label: screenshotPacketLabel ?? "Screenshot packet (PDF)",
                sub: screenshotPacketSub ?? "Every captured court page in one PDF.",
                icon: ICON_PICTURE,
                tone: TONES.internal,
                href: screenshotPacketHref,
                download: true,
              }]
            : []),
        ],
      },
    ],
  };

  // Drill state — array of branch IDs from the root downward.
  const [path, setPath] = useState<string[]>([]);

  // Walk to the current branch by following the path.
  const current = useMemo<Branch>(() => {
    let node: Branch = root;
    for (const id of path) {
      const next = node.children.find((c): c is Branch => c.kind === "branch" && c.id === id);
      if (!next) break;
      node = next;
    }
    return node;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return (
    <div
      data-dock-actions
      style={{
        position: "absolute",
        bottom: "calc(100% + 12px)",
        right: 0,
        // Wider than the old menu — the bigger fonts + 44px icon tiles
        // need more horizontal headroom to read comfortably.
        width: "min(420px, calc(100vw - 16px))",
        maxHeight: "min(72vh, 720px)",
        overflowY: "auto",
        background: "rgba(20,18,16,0.97)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 14,
        padding: 10,
        boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        animation: "dock-pop-in 0.18s ease-out both",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(255,255,255,0.22) transparent",
      }}
    >
      {/* Breadcrumb header — only renders when drilled in. Shows a
          back button + the current branch's label/sub so the user
          always knows where they are. */}
      {path.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "6px 4px 12px 4px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            onClick={() => setPath((p) => p.slice(0, -1))}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 999,
              color: "rgba(255,255,255,0.92)",
              fontFamily: "var(--font-body)",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              letterSpacing: 0.2,
              flexShrink: 0,
            }}
          >
            ← Back
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 19,
                fontWeight: 600,
                color: current.tone.label,
                lineHeight: 1.15,
              }}
            >
              {current.label}
            </div>
            {current.sub && (
              <div
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontSize: 13,
                  color: "rgba(255,255,255,0.55)",
                  marginTop: 2,
                }}
              >
                {current.sub}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        // key on path so the items remount + re-stagger on each drill
        key={path.join("/") || "root"}
        style={{ display: "flex", flexDirection: "column", gap: 4 }}
      >
        {current.children.map((node, i) => (
          <div
            key={node.id}
            style={{
              animation: `dock-item-land 460ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 90}ms both`,
            }}
          >
            <ActionMenuItem
              node={node}
              onDrillIn={(id) => setPath((p) => [...p, id])}
            />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes dock-item-land {
          from { opacity: 0; transform: translateY(-6px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

// One row in the actions menu — leaf (button or anchor that fires the
// real action) OR branch (drills into the next layer of submenu).
function ActionMenuItem({
  node,
  onDrillIn,
}: {
  node:
    | { kind: "leaf"; id: string; label: string; sub: string; icon: JSX.Element; tone: { hi: string; tile: string; tileHot: string; label: string }; critical?: boolean; beta?: boolean; href?: string; onClick?: () => void; download?: boolean; busy?: boolean; busyLabel?: string }
    | { kind: "branch"; id: string; label: string; sub: string; icon: JSX.Element; tone: { hi: string; tile: string; tileHot: string; label: string }; children: unknown[] };
  onDrillIn: (id: string) => void;
}): JSX.Element {
  const tone = node.tone;
  const isCritical = node.kind === "leaf" && node.critical === true;
  const isBranch = node.kind === "branch";
  const isBusy = node.kind === "leaf" && node.busy === true;

  const inner = (
    <>
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: isCritical || isBranch ? tone.tileHot : tone.tile,
          color: tone.hi,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {isBusy ? (
          <svg width="20" height="20" viewBox="0 0 14 14" className="animate-spin" aria-hidden="true">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.25" fill="none" />
            <path d="M7 2a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        ) : node.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: isCritical ? tone.hi : tone.label,
            fontWeight: isCritical ? 700 : 600,
            fontSize: 17,
            fontFamily: "var(--font-serif)",
            lineHeight: 1.2,
          }}
        >
          {isBusy ? ((node as { busyLabel?: string }).busyLabel ?? "Working…") : node.label}
          {node.kind === "leaf" && node.beta && (
            <span
              style={{
                marginLeft: 8,
                display: "inline-block",
                fontSize: 10,
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.18em",
                background: "var(--color-orange-hi)",
                color: "white",
                borderRadius: 4,
                padding: "1px 6px",
                fontWeight: 700,
                verticalAlign: "middle",
              }}
            >
              BETA
            </span>
          )}
        </div>
        <div
          style={{
            color: "rgba(255,255,255,0.62)",
            fontSize: 14,
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            lineHeight: 1.4,
            marginTop: 3,
          }}
        >
          {node.sub}
        </div>
      </div>
      {isBranch && (
        <span
          aria-hidden="true"
          style={{
            color: "rgba(255,255,255,0.45)",
            fontSize: 22,
            fontFamily: "var(--font-mono)",
            flexShrink: 0,
            paddingLeft: 4,
          }}
        >
          ›
        </span>
      )}
    </>
  );

  const sharedStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    padding: "14px 14px",
    background: "transparent",
    border: "none",
    borderRadius: 10,
    cursor: isBusy ? "wait" : "pointer",
    textDecoration: "none",
    color: "inherit",
    textAlign: "left",
    transition: "background 0.15s ease",
  };

  if (node.kind === "branch") {
    return (
      <button
        type="button"
        onClick={() => onDrillIn(node.id)}
        style={sharedStyle}
        className="hover:bg-white/8"
      >
        {inner}
      </button>
    );
  }
  if (node.href) {
    const isPreview =
      node.href.startsWith("/intake") ||
      node.href.startsWith("/client-summary") ||
      node.href.startsWith("/assessment");
    const useDownload = node.download ?? !isPreview;
    // Server-generated downloads (PDFs, .zip packets) often take 1-5
    // seconds. Route them through the global download toaster so the
    // user sees a spinner + "Generating…" label + auto-dismissing
    // success message, instead of clicking and wondering whether the
    // app froze. Preview links + plain client-side routes still use
    // the native anchor behavior.
    const useToast = useDownload && node.href.startsWith("/api/");
    return (
      <a
        href={node.href}
        target={isPreview ? "_blank" : undefined}
        rel={isPreview ? "noreferrer" : undefined}
        download={useDownload && !useToast ? "" : undefined}
        onClick={(e) => {
          if (useToast && node.href) {
            e.preventDefault();
            const verb = inferToastVerb(node.id);
            void showDownloadToast({
              label: `${verb} ${node.label}`,
              url: node.href,
            });
          }
          node.onClick?.();
        }}
        style={sharedStyle}
        className="hover:bg-white/8"
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={node.onClick}
      disabled={isBusy}
      style={sharedStyle}
      className="hover:bg-white/8"
    >
      {inner}
    </button>
  );
}

// ── Icon constants — bigger 22px viewport, 1.6 stroke. ────────────────
const ICON_MIC = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="8" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 11a6 6 0 0 0 12 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M11 17v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const ICON_REC = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="9" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="11" cy="11" r="3.5" fill="currentColor" />
  </svg>
);
const ICON_HANDOUT = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M5 4h9l3 3v11H5V4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M14 4v3h3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 11h7M7 14h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ICON_DOC = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="4" y="3" width="14" height="16" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 7h8M7 10h8M7 13h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ICON_DOC_ES = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="4" y="3" width="14" height="16" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 7h8M7 10h8M7 13h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <text x="11" y="18.2" textAnchor="middle" fontSize="5" fill="currentColor" fontFamily="sans-serif" fontWeight="700">ES</text>
  </svg>
);
const ICON_EYE = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M2 11s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <circle cx="11" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const ICON_RESUME = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="4" y="3" width="14" height="16" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="11" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 13h8M7 16h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ICON_PAPER_PLANE = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M2 11l18-7-7 17-2-7-9-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const ICON_ENVELOPE = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 6l8 7 8-7" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const ICON_LETTER_DOLLAR = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M5 3h9l3 3v13H5V3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M14 3v3h3" stroke="currentColor" strokeWidth="1.6" />
    <text x="11" y="16" textAnchor="middle" fontSize="7" fill="currentColor" fontFamily="serif" fontWeight="700">$</text>
  </svg>
);
const ICON_LETTER_DOT = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M5 3h9l3 3v13H5V3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M14 3v3h3" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="14" cy="15" r="2" fill="currentColor" />
  </svg>
);
const ICON_CAR = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="2" y="7" width="18" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="6" cy="17" r="1.6" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="16" cy="17" r="1.6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5 10h12M5 13h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ICON_SHIELD = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M11 2L3 5v5c0 5 4 9 8 10 4-1 8-5 8-10V5L11 2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M8 11l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICON_PERSON = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <circle cx="11" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 20c0-4 4-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const ICON_DOC_CHECK = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M5 3h9l3 3v13H5V3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M14 3v3h3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ICON_PACKET = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M3 6l8-3 8 3v10l-8 3-8-3V6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M3 6l8 3 8-3M11 9v10" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const ICON_FOLDER = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M3 6h6l2-2h8v13H3V6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const ICON_DOWNLOAD = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M11 3v12M11 15l-5-5M11 15l5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const ICON_CLIPBOARD = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="14" height="15" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <rect x="8" y="2.5" width="6" height="3" rx="0.8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M7 10h8M7 13h8M7 16h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const ICON_PICTURE = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="16" height="13" rx="1.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8" cy="9" r="1.6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3 14l4-3 3 2 4-3 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none" />
  </svg>
);
const ICON_NOTE = (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <path d="M4 3h11l3 3v13H4V3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M15 3v3h3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7 9h8M7 12h8M7 15h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
